import {
	Component, Input, Output, EventEmitter, OnChanges, SimpleChanges,
	ChangeDetectorRef, ChangeDetectionStrategy, SimpleChange
} from '@angular/core';
import { ComponentBase } from 'common';
import { DiscResultsService, ExportToPrtgMapOptions } from 'discovery/services/discresults.service';
import { PrtgSettingsSummaryDto } from 'UvExplorerServer.Dtos.TS/Session/PrtgSettingsSummaryDto';

// Dialog for the map-level "Export to PRTG Map ..." context-menu action.
// Layout mirrors the WinForms ExportToPrtgMapForm: PRTG Server panel + Export
// Settings panel (Map Name + Browse, overwrite/labels + icon size, target
// group + Browse, Create PING sensor). Both Browse buttons open the same
// PRTG group picker — for Map Name it fills the input with the chosen
// group's name; for the target group it fills the group field similarly.
@Component({
	selector: 'export-to-prtg-map',
	templateUrl: './export-to-prtg-map.component.html',
	styleUrls: ['./export-to-prtg-map.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportToPrtgMapComponent extends ComponentBase implements OnChanges {

	@Input() public display: boolean;
	@Input() public netViewGuid: string;
	@Input() public defaultMapName: string;

	@Output() public displayChange = new EventEmitter<boolean>();
	@Output() public onCancel = new EventEmitter<void>();
	@Output() public onExport = new EventEmitter<{
		prtgSettingsGuid: string,
		mapName: string,
		prtgTargetGroupId: string,
		options: ExportToPrtgMapOptions
	}>();

	public dropdownOptions: Array<{ label: string, value: string, disabled: boolean }> = [];
	public selectedGuid: string = "";
	public mapName: string = "";
	public prtgTargetGroupId: string = "";
	public loading: boolean = false;
	public errorMessage: string = "";

	public serverSummary: string = "";
	private summariesByGuid: { [guid: string]: PrtgSettingsSummaryDto } = {};

	public options: ExportToPrtgMapOptions = this.buildDefaultOptions();

	// Picker dialog state. The Map Name "Browse..." opens the PRTG Maps
	// picker (flat list); the Group "Browse..." opens the PRTG Groups picker
	// (tree). Each picker tracks its own visibility flag.
	public mapPickerVisible: boolean = false;
	public groupPickerVisible: boolean = false;
	public groupPickerTitle: string = "";

	constructor(
		private discResultsService: DiscResultsService,
		private changeDetector: ChangeDetectorRef) {
		super();
		this.display = false;
		this.netViewGuid = "";
		this.defaultMapName = "";
	}

	public ngOnChanges(changes: SimpleChanges): void {
		const displayChange: SimpleChange = changes["display"];
		if (displayChange && displayChange.currentValue === true && this.netViewGuid) {
			this.mapName = this.defaultMapName || "";
			this.prtgTargetGroupId = "New UVexplorer Devices";
			this.options = this.buildDefaultOptions();
			this.loadPrtgSettings();
		}
	}

	private buildDefaultOptions(): ExportToPrtgMapOptions {
		return {
			overwriteExistingTopologyMap: false,
			showIfLabels: false,
			defaultIconHeight: 75,
			defaultIconWidth: 75,
			addPingSensors: true,
		};
	}

	private loadPrtgSettings(): void {
		this.loading = true;
		this.errorMessage = "";
		this.dropdownOptions = [];
		this.selectedGuid = "";
		this.summariesByGuid = {};
		this.serverSummary = "";
		this.changeDetector.markForCheck();

		this.discResultsService.listPrtgSettings(this.netViewGuid).subscribe(
			(summaries: PrtgSettingsSummaryDto[]) => {
				this.loading = false;
				if (!summaries || summaries.length === 0) {
					this.errorMessage = "No PRTG settings are configured for this network. Add one in Protocol Settings first.";
				} else {
					this.dropdownOptions = summaries.map(s => ({
						label: this.formatLabel(s),
						value: s.prtg_settings_guid,
						disabled: false,
					}));
					this.summariesByGuid = {};
					for (const s of summaries) this.summariesByGuid[s.prtg_settings_guid] = s;
					this.selectedGuid = this.dropdownOptions[0].value;
					this.updateServerSummary();
				}
				this.changeDetector.markForCheck();
			},
			(error) => {
				this.loading = false;
				this.errorMessage = "Failed to load PRTG agents.";
				this.changeDetector.markForCheck();
			});
	}

	public onPrtgSettingsChanged(): void {
		this.updateServerSummary();
		this.changeDetector.markForCheck();
	}

	private updateServerSummary(): void {
		const s = this.summariesByGuid[this.selectedGuid];
		if (!s) {
			this.serverSummary = "";
			return;
		}
		const url = s.server_url || "(no URL)";
		this.serverSummary = "Server URL: " + url + (s.is_hosted_prtg ? " (hosted PRTG)" : "");
	}

	private formatLabel(s: PrtgSettingsSummaryDto): string {
		let base = s.name || s.server_url || "(unnamed PRTG agent)";
		if (s.is_hosted_prtg) base += " — hosted PRTG";
		return base;
	}

	public get enableExport(): boolean {
		return !this.loading && !!this.selectedGuid && !!this.mapName && this.mapName.trim().length > 0;
	}

	public hide(): void {
		this.display = false;
		this.displayChange.emit(this.display);
		this.changeDetector.markForCheck();
	}

	public cancel(): void {
		this.hide();
		this.onCancel.emit();
	}

	public export(): void {
		if (!this.enableExport) return;
		const chosenGuid = this.selectedGuid;
		const chosenName = this.mapName.trim();
		const chosenTargetGroup = (this.prtgTargetGroupId || "").trim();
		const chosenOptions = { ...this.options };
		this.hide();
		this.onExport.emit({
			prtgSettingsGuid: chosenGuid,
			mapName: chosenName,
			prtgTargetGroupId: chosenTargetGroup,
			options: chosenOptions,
		});
	}

	// ── Browse... pickers ─────────────────────────────────────────────────

	public browseMapName(): void {
		if (!this.selectedGuid) return;
		this.mapPickerVisible = true;
	}

	public browseTargetGroup(): void {
		if (!this.selectedGuid) return;
		this.groupPickerTitle = "Select Target Group";
		this.groupPickerVisible = true;
	}

	public onMapPicked(event: { id: number, name: string }): void {
		this.mapName = event.name;
		this.changeDetector.markForCheck();
	}

	public onGroupPicked(event: { id: number, name: string }): void {
		this.prtgTargetGroupId = event.name;
		this.changeDetector.markForCheck();
	}
}
